import { Box, Drawer } from '@mui/material';
import { Typography } from 'src/components/new/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { TextField } from 'src/components/new/inputs/TextField';
import BkSelect from 'src/components/select/BkSelect';
import { LabeledCheckbox } from 'src/components/new/inputs/Checkbox';
import { Button } from 'src/components/new/Button';
import { useState, useEffect } from 'react';
import { Spacer } from 'src/components/new/Spacer';
import { stateMapper } from 'src/utils/constant';
import { PafResponse, PafValues } from 'src/features/company/constants';
import { useMask, format } from '@react-input/mask';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

interface AddPAFProps {
  open: boolean;
  existingPAFData?: PafResponse | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const NAICS_OPTIONS = [
  '541110 - Law Offices',
  '525920 - Bankruptcy Estates',
  '921110 - Executive Govt. Offices',
  '922190 - Justice, Public Order, Safety',
  '922110 - Court Systems',
];

const fieldNameMapper = {
  companyName: 'Law Firm/Company Name',
  address: 'Address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP',
  phone: 'Phone',
  naicsNumber: 'NAICS Number',
  email: 'Email',
  websiteUrl: 'Website/URL',
  eSignatureFullName: 'Full Name',
  eSignatureTitle: 'Title',
};

interface FormDataErrors {
  companyName: '';
  address: '';
  city: '';
  state: '';
  zipCode: '';
  phone: '';
  naicsNumber: '';
  email: '';
  websiteUrl: '';
  uspsMailerId: '';
  parentCompany: '';
  eSignatureFullName: '';
  eSignatureTitle: '';
  consent: '';
}

const AddPAF: React.FC<AddPAFProps> = ({ existingPAFData, open, onClose, onSubmit }) => {
  const phoneInputRef = useMask({
    mask: '(___) ___-____',
    replacement: { _: /\d/ },
    showMask: true,
  });

  const [formData, setFormData] = useState<PafValues>({
    companyName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    naicsNumber: '',
    email: '',
    websiteUrl: '',
    uspsMailerId: '',
    parentCompany: '',
    eSignatureFullName: '',
    eSignatureTitle: '',
    consent: false,
  });

  // Update form data when existingPAFData changes
  useEffect(() => {
    if (existingPAFData) {
      setFormData({
        companyName: existingPAFData.companyName || '',
        address: existingPAFData.address || '',
        city: existingPAFData.city || '',
        state: existingPAFData.state || '',
        zipCode: existingPAFData.zipCode || '',
        phone: existingPAFData.phone
          ? format(existingPAFData.phone, { replacement: { _: /\d/ }, mask: '(___) ___-____' })
          : '',
        naicsNumber: existingPAFData.naicsNumber || '',
        email: existingPAFData.email || '',
        websiteUrl: existingPAFData.websiteUrl || '',
        uspsMailerId: existingPAFData.uspsMailerId || '',
        parentCompany: existingPAFData.parentCopmany || '',
        eSignatureFullName: existingPAFData.eSignatureFullName || '',
        eSignatureTitle: existingPAFData.eSignatureTitle || '',
        consent: false,
      });
    }
  }, [existingPAFData]);

  const [formDataErrors, setFormDataErrors] = useState<FormDataErrors>({
    companyName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    naicsNumber: '',
    email: '',
    websiteUrl: '',
    uspsMailerId: '',
    parentCompany: '',
    eSignatureFullName: '',
    eSignatureTitle: '',
    consent: '',
  });

  const handleTextfieldChange = (e: any) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCheckboxChange = () => {
    setFormData((prev) => ({ ...prev, consent: !prev.consent }));
  };

  const handleSelectChange = (e: any) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    const newErrors: any = {};
    Object.entries(formData).forEach(([name, value]) => {
      if (!value) {
        if (
          name !== 'parentCompany' &&
          name !== 'uspsMailerId' &&
          name !== 'eSignatureFullName' &&
          name !== 'eSignatureTitle' &&
          name !== 'websiteUrl'
        ) {
          newErrors[name] = `${fieldNameMapper[name as keyof typeof fieldNameMapper]} is required`;
        }
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Provided email is not valid.';
    }

    if (formData.websiteUrl && !/^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*$/.test(formData.websiteUrl)) {
      newErrors.websiteUrl = 'Provided Website is not valid';
    }

    if (formData.phone) {
      const parsedPhone = parsePhoneNumberFromString(formData.phone, 'US');
      if (!parsedPhone?.isValid()) {
        newErrors.phone = 'Wrong phone format';
      }
    }

    setFormDataErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== '');

    if (!hasErrors) {
      const strippedPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
      onSubmit({ ...formData, phone: strippedPhone });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          background: '#FFF',
          width: '40%',
          maxWidth: '40%',
        },
      }}
    >
      <Box sx={{ padding: '1.25rem' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <Typography variant="h6">Postal Acknowledgment Form</Typography>
          <button style={{ all: 'unset', cursor: 'pointer' }} onClick={onClose}>
            <CloseIcon />
          </button>
        </Box>
        <Box>
          <Typography fontSize={13} sx={{ color: '#45464f', marginBottom: 2 }}>
            The collection of information on this Processing Acknowledgement Form (PAF) is required by the Privacy Act
            of 1974. The United States Postal Service (USPS) requires that each NCOALink Mail Processing Equipment
            Service Provider Licensee have a completed NCOALink MPE PAF for each of their NCOALink customers prior to
            providing the NCOALink services. The Licensee is also required by the USPS to retain a copy of the completed
            form for each of its customers and to obtain an updated PAF from each customer at a minimum once per year.
            Any signature upon this PAF shall be considered valid for all purposes and have the same effect whether it
            is an ink-signed hardcopy document or equivalent alternative.
          </Typography>
          <Typography fontSize={13} fontWeight={500} sx={{ marginBottom: 3 }}>
            I, the undersigned, an authorized representative of:
          </Typography>
          <TextField
            label="Law Firm/Company Name"
            placeholder="Add..."
            required={true}
            id="companyName"
            name="companyName"
            error={formDataErrors.companyName}
            onChange={handleTextfieldChange}
            value={formData.companyName}
          />
          <TextField
            label="Address"
            placeholder="Add..."
            required={true}
            id="address"
            name="address"
            onChange={handleTextfieldChange}
            error={formDataErrors.address}
            value={formData.address}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              containerSX={{ flex: 3 }}
              label="City"
              placeholder="Add..."
              required={true}
              id="city"
              name="city"
              error={formDataErrors.city}
              onChange={handleTextfieldChange}
              value={formData.city}
            />
            <Box sx={{ marginBottom: '1.25rem', flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  fontFamily: 'Instrument Sans',
                  lineHeight: '1.25rem',
                  color: formDataErrors.state ? '#AC3737' : '#45464F',
                  marginBottom: '0.25rem',
                }}
              >
                State*
              </Typography>
              <BkSelect
                customStyles={{
                  '& .MuiInputBase-input': {
                    borderRadius: '0.375rem',
                    border: formDataErrors.state ? '0.0625rem solid #AC3737' : '0.0625rem solid #7E828B',
                    fontSize: '1rem',
                    fontWeight: 400,
                    fontFamily: 'Instrument Sans',
                    lineHeight: '1.5rem',
                    padding: '0.75rem 1rem',
                  },
                }}
                placeholder="Select"
                error={formDataErrors.state}
                value={formData.state}
                name="state"
                data={Object.values(stateMapper)}
                onChange={handleSelectChange}
                disabled={false}
              />
            </Box>
            <TextField
              containerSX={{ flex: 1 }}
              label="ZIP"
              placeholder="Add..."
              required={true}
              id="zipCode"
              onChange={handleTextfieldChange}
              name="zipCode"
              error={formDataErrors.zipCode}
              value={formData.zipCode}
            />
          </Box>
          <TextField
            label="Phone"
            placeholder="(___) ___-____"
            required={true}
            id="phone"
            name="phone"
            inputRef={phoneInputRef}
            onChange={handleTextfieldChange}
            error={formDataErrors.phone}
            value={formData.phone}
          />
          <Box sx={{ marginBottom: '1.25rem' }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                fontFamily: 'Instrument Sans',
                lineHeight: '1.25rem',
                color: formDataErrors.naicsNumber ? '#AC3737' : '#45464F',
                marginBottom: '0.25rem',
              }}
            >
              NAICS Number*
            </Typography>
            <BkSelect
              customStyles={{
                '& .MuiInputBase-input': {
                  borderRadius: '0.375rem',
                  border: formDataErrors.naicsNumber ? '0.0625rem solid #AC3737' : '0.0625rem solid #7E828B',
                  fontSize: '1rem',
                  fontWeight: 400,
                  fontFamily: 'Instrument Sans',
                  lineHeight: '1.5rem',
                  padding: '0.75rem 1rem',
                },
              }}
              placeholder="Select"
              error={formDataErrors.naicsNumber}
              value={formData.naicsNumber}
              name="naicsNumber"
              data={NAICS_OPTIONS}
              onChange={handleSelectChange}
              disabled={false}
            />
          </Box>
          <TextField
            label="Email"
            placeholder="Add..."
            required={true}
            id="email"
            name="email"
            error={formDataErrors.email}
            onChange={handleTextfieldChange}
            value={formData.email}
          />
          <TextField
            label="Website/URL"
            placeholder="Add..."
            onChange={handleTextfieldChange}
            id="websiteUrl"
            name="websiteUrl"
            error={formDataErrors.websiteUrl}
            value={formData.websiteUrl}
          />
          <TextField
            label="USPS Mailer ID"
            placeholder="Add..."
            id="uspsMailerId"
            name="uspsMailerId"
            error={formDataErrors.uspsMailerId}
            value={formData.uspsMailerId}
            onChange={handleTextfieldChange}
          />
          <TextField
            label="Parent Company"
            placeholder="Add..."
            onChange={handleTextfieldChange}
            id="parentCompany"
            name="parentCompany"
            error={formDataErrors.parentCompany}
            value={formData.parentCompany}
          />
        </Box>
        <Typography fontSize={13} sx={{ color: '#45464f', marginBottom: 2 }}>
          do hereby acknowledge that I have received and reviewed the NCOA Link Mail Processing Equipment Information
          Package supplied to me by BK Attorney Services, LLC, and NCOA Link MPE Data User Licensee.
        </Typography>
        <a
          style={{ fontSize: 12, color: '#436eb2' }}
          target="_blank"
          href="/NCOA_Form.pdf"
        >
          Click here to read the full NCOA link information package
        </a>
        <Typography fontSize={13} sx={{ color: '#45464f', marginBottom: 2, marginTop: 2 }}>
          I also understand that the sole purpose of the NOCA Link MPE service is to provide:
        </Typography>
        <Typography fontSize={13} sx={{ color: '#45464f', marginBottom: 2 }}>
          (1) Mailpiece redirection (via re-addressing) due to customer moves for mailpieces that I have submitted to
          the Licensee for mailing;
        </Typography>
        <Typography fontSize={13} sx={{ color: '#45464f', marginBottom: 2 }}>
          (2) A mailpiece correction service (electronic return) for my customer addresses that will be used for
          preparation of future mailings. The mailpiece facsimiles that I have submitted to the Licensee will be
          returned within seven business days of processing, unless I authorize a longer time period in writing; OR
        </Typography>
        <Typography fontSize={13} sx={{ color: '#45464f', marginBottom: 2 }}>
          (3) Mailpiece address correction service in which mailpieces that obtain adrress correction information as a
          result of this process will be seperated from my mailing and returned either in the form hardcopy or
          photocopied mailpieces and returned within seven business days of processing by the Licensee, unless I
          authorize a longer time period in writing. The information provided to me for thsi service will be used for
          preparation of future mailings.
        </Typography>
        <Typography fontSize={13} sx={{ color: '#45464f', marginBottom: 2 }}>
          Furthermore, I understand that the NCOA Link MPE process may not be used to create or maintain new movers'
          lists. The identity of the licensee is as follows: Business Name (Mail Processor): BK Attorney Services, LLC
          (d/b/a CertificateofService.com). The name of the authorized representative is Victoria Blake, CEO BK Attorney
          Services, LLC.
        </Typography>
        <Typography fontSize={18} fontWeight={500} sx={{ borderBottom: '0.0625rem solid #ece9dc', marginBottom: 2 }}>
          eSignature (Sign and Submit)
        </Typography>
        <TextField
          label="Your Full Name: (for eSignature)"
          placeholder="Add..."
          id="eSignatureFullName"
          name="eSignatureFullName"
          onChange={handleTextfieldChange}
          error={formDataErrors.eSignatureFullName}
          value={formData.eSignatureFullName}
        />
        <TextField
          label="Your Title:"
          onChange={handleTextfieldChange}
          placeholder="Add..."
          id="eSignatureTitle"
          name="eSignatureTitle"
          error={formDataErrors.eSignatureTitle}
          value={formData.eSignatureTitle}
        />
        <LabeledCheckbox
          label={`By checking this box and clicking on the 'submit' button below, you are authorizing BK Attorney Services, LLC to handle your mailings per the rules determined by the USPS under their Domestic Mail Manual. Your check in this box constitutes your electronic signature under the Uniform Electronic Transactions Act ("EUTA"), the U.S. Esign Act of 2000, and for residents of New York, Washington State, and Illinois, the Electronic Signatures and Records Act ("ESRA"), RCW 19.34 (The Washington Electronic Authentication Act), and 5 ICLS 175 (The Electronic Commerce Security Act), respectively.`}
          checked={formData.consent}
          onChange={handleCheckboxChange}
        />
        {formDataErrors.consent && (
          <Typography sx={{ color: '#AC3737' }} fontSize={14}>
            Agree in order to proceed
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          height: '9.375rem',
          backgroundColor: '#fcfcfc',
          borderTop: '0.0625rem solid #d6d2db',
          paddingY: 2,
          display: 'flex',
          justifyContent: 'space-around',
        }}
      >
        <Spacer w={150} />
        <Button onClick={handleSubmit} sx={{ width: '18.75rem' }} type="primary">
          Submit PAF Form
        </Button>
      </Box>
    </Drawer>
  );
};

export default AddPAF;
